/**
 * useAnchoredPanel — the one implementation of "a floating panel anchored to a
 * trigger button, portalled to `document.body`".
 *
 * Why a portal at all: a panel positioned with `absolute` inside a scrollable
 * ancestor (a `<Modal>` body, a table's `overflow-x-auto` wrapper, the sidebar)
 * gets clipped by that ancestor's `overflow` the moment it would render past
 * the visible edge. The alternative — turning off the ancestor's scrolling —
 * trades a clipped panel for unreachable content once the page is taller than
 * the viewport.
 *
 * Because the panel is `position: fixed`, it is also the ONLY thing that can
 * bound its own height: anything rendered past a viewport edge is unreachable,
 * since no ancestor can scroll to it. So the panel measures the space
 * available above/below the trigger on every open (and on scroll/resize),
 * flips to open upward when there is meaningfully more room there, and clamps
 * its height to what actually fits. Consumers MUST pair `panelStyle` with
 * `overflow-y-auto` so the clamped box scrolls internally.
 *
 * Note the algorithm never measures the panel itself — the flip decision comes
 * from the space around the trigger, not from the panel's height. That is what
 * lets this run in a single layout pass with no height estimate to keep in
 * sync with the markup.
 *
 * The hook owns positioning, outside-pointerdown dismissal and Escape. It does
 * NOT own `open`: the consumer keeps that state so its own keyboard handling
 * (roving focus, type-ahead, Tab-to-close) stays at the call site.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import type { CSSProperties, RefObject } from "react"

// ─── Layout constants ────────────────────────────────────────────────────────
// Panel geometry, in px. Named rather than inlined per `magic-values.md` §5 —
// each one encodes a layout decision, not an arbitrary number.

/** Gap between the trigger edge and the panel. */
export const PANEL_GAP = 4
/** Breathing room kept between the panel and the viewport edge. */
export const VIEWPORT_MARGIN = 8
/**
 * Tallest a panel ever gets, even with room to spare — matches the `max-h-60`
 * used by `<Combobox>` so every picker starts scrolling at the same point
 * instead of one sprawling to full viewport height.
 */
export const PANEL_MAX_HEIGHT = 240
/**
 * Floor for the clamped height. A trigger pinned near a viewport edge still
 * gets a usable, scrollable panel rather than a 20px sliver.
 */
export const PANEL_MIN_HEIGHT = 144

/** Elements that participate in the Tab sequence inside a trapped panel. */
const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export interface AnchoredPanelOptions {
  /** Whether the panel is currently rendered. The consumer owns this state. */
  open: boolean
  /** Called on outside pointer-down and on Escape. */
  onClose: () => void
  /** Which trigger edge the panel aligns to. Default "left". */
  align?: "left" | "right"
  /**
   * Panel width in px, when the panel has a fixed width. Supplying it enables
   * horizontal viewport clamping so a trigger near an edge can't push the
   * panel off-screen. Omit for intrinsically-sized panels.
   */
  width?: number
  /** Tallest the panel ever gets. Default {@link PANEL_MAX_HEIGHT}. */
  maxHeight?: number
  /** Floor for the clamped height. Default {@link PANEL_MIN_HEIGHT}. */
  minHeight?: number
  /** Gap between the trigger edge and the panel. Default {@link PANEL_GAP}. */
  gap?: number
  /**
   * Keep Tab inside the panel while open. Set for `role="dialog"` panels,
   * which own focus by definition. Menus should leave this off and close on
   * Tab instead, so focus returns to the document flow.
   *
   * Required for panels opened from inside a `<Modal>`: the modal's focus trap
   * only walks its own subtree, and a portalled panel is not in it — without
   * containment, Tab out of the panel escapes to the browser chrome.
   */
  trapFocus?: boolean
}

export interface AnchoredPanel {
  /** Attach to the trigger button. Drives positioning and focus restore. */
  triggerRef: RefObject<HTMLButtonElement>
  /** Attach to the portalled panel element. */
  panelRef: RefObject<HTMLDivElement>
  /**
   * Inline style for the panel: `position: fixed` offsets plus the measured
   * `maxHeight`. `null` until the first measure — render the portal only once
   * it is set, so the panel never paints at the wrong position.
   */
  panelStyle: CSSProperties | null
  /** Trigger width at last measure, for panels that match their trigger. */
  triggerWidth: number
  /** Close the panel and return focus to the trigger. */
  close: () => void
}

export function useAnchoredPanel({
  open,
  onClose,
  align = "left",
  width,
  maxHeight = PANEL_MAX_HEIGHT,
  minHeight = PANEL_MIN_HEIGHT,
  gap = PANEL_GAP,
  trapFocus = false,
}: AnchoredPanelOptions): AnchoredPanel {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [panelStyle, setPanelStyle] = useState<CSSProperties | null>(null)
  const [triggerWidth, setTriggerWidth] = useState(0)

  // Keep the latest onClose in a ref so the effects below don't tear down and
  // re-register their document listeners every time the consumer re-renders
  // with a new inline callback.
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  const close = useCallback(() => {
    onCloseRef.current()
    triggerRef.current?.focus()
  }, [])

  // Position from the trigger's rect. Recomputed on open and kept in sync
  // while open (scroll/resize) — the alternative (absolute + clip-through
  // overflow ancestors) is what this replaces.
  useLayoutEffect(() => {
    if (!open) {
      setPanelStyle(null)
      return
    }
    const place = () => {
      const el = triggerRef.current
      if (!el) return
      const r = el.getBoundingClientRect()

      // Measure available space against the VISUAL viewport, not the layout
      // one. On mobile the on-screen keyboard shrinks the visual viewport
      // while `window.innerHeight` stays put — measuring the latter places the
      // panel underneath the keyboard, which matters most for pickers that
      // focus a search field on open. The offsets below stay in layout-
      // viewport coordinates because that is what `position: fixed` resolves
      // against.
      const vv = window.visualViewport
      const viewTop = vv?.offsetTop ?? 0
      const viewBottom = viewTop + (vv?.height ?? window.innerHeight)

      // Space the panel could occupy on either side of the trigger without
      // running past a viewport edge.
      const spaceBelow = viewBottom - r.bottom - gap - VIEWPORT_MARGIN
      const spaceAbove = r.top - viewTop - gap - VIEWPORT_MARGIN
      // Open upward only when below is genuinely too cramped AND above is
      // roomier — so the common case (plenty of room below) never moves.
      const flipUp = spaceBelow < minHeight && spaceAbove > spaceBelow
      const available = flipUp ? spaceAbove : spaceBelow

      // Horizontal clamp is only possible for a known width; intrinsically
      // sized panels keep the plain trigger-edge alignment.
      const maxInset =
        width === undefined
          ? undefined
          : Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN)
      const clampInset = (inset: number) =>
        maxInset === undefined ? inset : Math.min(Math.max(VIEWPORT_MARGIN, inset), maxInset)

      setTriggerWidth(r.width)
      setPanelStyle({
        position: "fixed",
        top: flipUp ? undefined : r.bottom + gap,
        bottom: flipUp ? window.innerHeight - r.top + gap : undefined,
        left: align === "right" ? undefined : clampInset(r.left),
        right: align === "right" ? clampInset(window.innerWidth - r.right) : undefined,
        maxHeight: Math.min(maxHeight, Math.max(minHeight, available)),
      })
    }

    // Scrolling *inside* the panel doesn't move the trigger, and the capture
    // listener below would otherwise re-measure on every wheel tick now that
    // the panel scrolls internally.
    const onScroll = (e: Event) => {
      if (panelRef.current?.contains(e.target as Node)) return
      place()
    }
    place()
    window.addEventListener("scroll", onScroll, true)
    window.addEventListener("resize", place)
    // The keyboard opening/closing fires on visualViewport, not on window.
    window.visualViewport?.addEventListener("resize", place)
    window.visualViewport?.addEventListener("scroll", place)
    return () => {
      window.removeEventListener("scroll", onScroll, true)
      window.removeEventListener("resize", place)
      window.visualViewport?.removeEventListener("resize", place)
      window.visualViewport?.removeEventListener("scroll", place)
    }
  }, [open, align, width, maxHeight, minHeight, gap])

  // Close on outside pointer-down (covers touch + mouse). The panel is
  // portalled outside the trigger's subtree, so it needs its own contains().
  useEffect(() => {
    if (!open) return
    const handler = (e: PointerEvent) => {
      const target = e.target as Node
      if (triggerRef.current?.contains(target)) return
      if (panelRef.current?.contains(target)) return
      onCloseRef.current()
    }
    document.addEventListener("pointerdown", handler)
    return () => document.removeEventListener("pointerdown", handler)
  }, [open])

  // Escape via a capture-phase document listener. Capture always runs before
  // any bubble-phase listener — including a `<Modal>`'s own document-level
  // Escape handler — so Escape closes just this panel instead of also closing
  // an ancestor modal in the same keypress. Required specifically because the
  // panel is portalled to `document.body`: synthetic-event bubbling through
  // the React tree isn't guaranteed to run before a plain
  // `document.addEventListener` registered outside React.
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return
      e.preventDefault()
      e.stopPropagation()
      close()
    }
    document.addEventListener("keydown", handler, true)
    return () => document.removeEventListener("keydown", handler, true)
  }, [open, close])

  // Tab containment for dialog-semantics panels. Capture phase for the same
  // reason as Escape: a `<Modal>` ancestor runs its own Tab trap on document,
  // and it only knows about its own subtree.
  useEffect(() => {
    if (!open || !trapFocus) return
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return
      const panel = panelRef.current
      if (!panel) return
      // `tabIndex < 0` drops the cells a roving-tabindex grid parks out of the
      // Tab order (see `<IconPicker>`), so the wrap points below are the same
      // elements the browser's own Tab sequence would land on.
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.tabIndex >= 0,
      )
      if (focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement
      // Focus outside the panel entirely (the trigger, or the modal behind
      // it) — pull it back in rather than letting Tab wander.
      if (!panel.contains(active)) {
        e.preventDefault()
        e.stopPropagation()
        ;(e.shiftKey ? last : first).focus()
        return
      }
      if (e.shiftKey && active === first) {
        e.preventDefault()
        e.stopPropagation()
        last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault()
        e.stopPropagation()
        first.focus()
      }
    }
    document.addEventListener("keydown", handler, true)
    return () => document.removeEventListener("keydown", handler, true)
  }, [open, trapFocus])

  return { triggerRef, panelRef, panelStyle, triggerWidth, close }
}
